import { redirect, RouteObject } from 'react-router';
import PermissionGuard from '../gate/PermissionGuard';
import { PERMISSIONS } from '@/shared/constants';

const appUsersRoutes: RouteObject[] = [
	{
		path: 'app-users',
		lazy: async () => {
			const { default: Page } = await import('@/features/end-users/users/pages/UsersPage');
			return {
				Component: () => (
					<PermissionGuard permission={PERMISSIONS.USER_READ}>
						<Page />
					</PermissionGuard>
				),
			};
		},
		children: [
			{
				index: true,
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/users/pages/UsersIndexPage'
					);
					return { Component };
				},
			},
			{
				path: ':userId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/users/pages/UserDetailsPage'
					);
					return { Component };
				},
				children: [
					{
						index: true,
						loader: () => redirect('activity'),
					},
					{
						path: 'activity',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/users/pages/UserActivityPage'
							);
							return { Component };
						},
					},
					{
						path: 'information',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/users/pages/UserDetailsInfoPage'
							);
							return { Component };
						},
					},
				],
			},
			{
				path: ':userId/activity/:activityId',
				lazy: async () => {
					const { default: Page } = await import(
						'@/features/end-users/users/pages/UserActivityDetailsPage'
					);
					return {
						Component: () => (
							<PermissionGuard permission={PERMISSIONS.TRANSACTION_READ}>
								<Page />
							</PermissionGuard>
						),
					};
				},
			},
		],
	},
];

const tagsNfcRoutes: RouteObject[] = [
	{
		path: 'tags-nfc',
		lazy: async () => {
			const { default: Page } = await import('@/features/end-users/tags-nfc/pages/TagsNfcPage');
			return {
				Component: () => (
					<PermissionGuard permission={PERMISSIONS.TAG_READ}>
						<Page />
					</PermissionGuard>
				),
			};
		},
		children: [
			{
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/tags-nfc/pages/TagsNfcIndexPage'
					);
					return { Component };
				},
				children: [
					{
						index: true,
						loader: () => redirect('overview'),
					},
					{
						path: 'overview',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/tags-nfc/pages/TagsNfcOverviewPage'
							);
							return { Component };
						},
					},
					{
						path: 'whitelist',
						lazy: async () => {
							const { default: Page } = await import(
								'@/features/end-users/tags-nfc/pages/TagsNfcAllWhitelistPage'
							);
							return {
								Component: () => (
									<PermissionGuard permission={[PERMISSIONS.WHITELIST_READ]}>
										<Page />
									</PermissionGuard>
								),
							};
						},
					},
				],
			},
			{
				path: 'whitelist/details',
				lazy: async () => {
					const { default: Page } = await import(
						'@/features/end-users/tags-nfc/pages/TagsNFCWhitelistIndex'
					);
					return {
						Component: () => (
							<PermissionGuard permission={[PERMISSIONS.WHITELIST_READ]}>
								<Page />
							</PermissionGuard>
						),
					};
				},
				children: [
					{
						index: true,
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/tags-nfc/pages/TagsNfcOneWhitelistPage'
							);
							return { Component };
						},
					},
					{
						path: ':tagId',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/tags-nfc/pages/TagsNfcWhitelistDetailsPage'
							);
							return { Component };
						},
						children: [
							{
								index: true,
								loader: () => redirect('activity'),
							},
							{
								path: 'activity',
								lazy: async () => {
									const { default: Component } = await import(
										'@/features/end-users/tags-nfc/pages/TagsNfcWhitelistActivityPage'
									);
									return { Component };
								},
							},
							{
								path: 'information',
								lazy: async () => {
									const { default: Component } = await import(
										'@/features/end-users/tags-nfc/pages/TagsNfcInfoPage'
									);
									return { Component };
								},
							},
						],
					},
				],
			},
			{
				path: ':tagId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/tags-nfc/pages/TagsNfcDetailsPage'
					);
					return { Component };
				},
				children: [
					{
						index: true,
						loader: () => redirect('activity'),
					},
					{
						path: 'activity',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/tags-nfc/pages/TagsNfcTagsActivityPage'
							);
							return { Component };
						},
					},
					{
						path: 'information',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/tags-nfc/pages/TagsNfcInfoPage'
							);
							return { Component };
						},
					},
				],
			},
			{
				path: ':tagId/activity/:activityId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/tags-nfc/pages/TagsNfcActivityDetailsPage'
					);
					return { Component };
				},
			},
			{
				path: 'whitelist/details/:tagId/activity/:activityId',
				lazy: async () => {
					const { default: Page } = await import(
						'@/features/end-users/tags-nfc/pages/TagsNfcActivityDetailsPage'
					);
					return {
						Component: () => (
							<PermissionGuard permission={[PERMISSIONS.TRANSACTION_READ]}>
								<Page />
							</PermissionGuard>
						),
					};
				},
			},
			{},
		],
	},
];

const userSupportRoutes: RouteObject[] = [
	{
		path: 'user-support',
		lazy: async () => {
			const { default: Page } = await import(
				'@/features/end-users/user-support/pages/UserSupportPage'
			);
			return {
				Component: () => (
					<PermissionGuard permission={PERMISSIONS.TICKET_READ}>
						<Page />
					</PermissionGuard>
				),
			};
		},
		children: [
			{
				index: true,
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/user-support/pages/UserSupportIndexPage'
					);
					return { Component };
				},
			},
			{
				path: ':ticketId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/user-support/pages/UserSupportDetailsPage'
					);
					return { Component };
				},
			},
			{
				path: 'activity/:activityId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/user-support/pages/UserSupportActivityDetailsPage'
					);
					return { Component };
				},
			},
		],
	},
];

const qrsRoutes: RouteObject[] = [
	{
		path: 'qrs',
		lazy: async () => {
			const { default: Page } = await import('@/features/end-users/qrs/pages/QrsPage');
			return {
				Component: () => (
					<PermissionGuard permission={PERMISSIONS.QR_READ}>
						<Page />
					</PermissionGuard>
				),
			};
		},
		children: [
			{
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/qrs/pages/QrsIndexPage'
					);
					return { Component };
				},
				children: [
					{
						index: true,
						loader: () => redirect('overview'),
					},
					{
						path: 'overview',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/qrs/pages/QrsOverviewPage'
							);
							return { Component };
						},
					},
					{
						path: 'whitelist',
						lazy: async () => {
							const { default: Page } = await import(
								'@/features/end-users/qrs/pages/QrsAllWhitelistPage'
							);
							return {
								Component: () => (
									<PermissionGuard permission={[PERMISSIONS.QR_READ, PERMISSIONS.WHITELIST_READ]}>
										<Page />
									</PermissionGuard>
								),
							};
						},
					},
				],
			},
			{
				path: 'whitelist/details',
				lazy: async () => {
					const { default: Page } = await import(
						'@/features/end-users/qrs/pages/QrsWhitelistIndex'
					);
					return {
						Component: () => (
							<PermissionGuard permission={[PERMISSIONS.QR_READ, PERMISSIONS.WHITELIST_READ]}>
								<Page />
							</PermissionGuard>
						),
					};
				},
				children: [
					{
						index: true,
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/qrs/pages/QrsOneWhitelistPage'
							);
							return { Component };
						},
					},
					{
						path: ':qrId',
						lazy: async () => {
							const { default: Component } = await import(
								'@/features/end-users/qrs/pages/QrsWhitelistDetailsPage'
							);
							return { Component };
						},
					},
				],
			},
			{
				path: ':qrId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/qrs/pages/QrsDetailsPage'
					);
					return { Component };
				},
			},
			{
				path: ':qrId/activity/:activityId',
				lazy: async () => {
					const { default: Component } = await import(
						'@/features/end-users/qrs/pages/QrsActivityDetailsPage'
					);
					return { Component };
				},
			},
			{
				path: 'whitelist/details/:qrId/activity/:activityId',
				lazy: async () => {
					const { default: Page } = await import(
						'@/features/end-users/qrs/pages/QrsActivityDetailsPage'
					);
					return {
						Component: () => (
							<PermissionGuard
								permission={[
									PERMISSIONS.QR_READ,
									PERMISSIONS.WHITELIST_READ,
									PERMISSIONS.TRANSACTION_READ,
								]}
							>
								<Page />
							</PermissionGuard>
						),
					};
				},
			},
		],
	},
];

export const usersRoutes: RouteObject[] = [
	{
		path: 'users',
		children: [
			{
				lazy: async () => {
					const { default: Component } = await import('@/features/end-users/pages/EndUsersPage');
					return { Component };
				},
				children: [
					{
						index: true,
						loader: () => redirect('app-users'),
					},
					...appUsersRoutes,
					...tagsNfcRoutes,
					...qrsRoutes,
					...userSupportRoutes,
				],
			},
		],
	},
];
